'use client'

import { useState, createContext, useContext, useEffect } from 'react'
import dynamic from 'next/dynamic'
import { usePathname } from 'next/navigation'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Toaster as Sonner } from '@/components/ui/sonner'
import { SidebarProvider } from '@/components/ui/sidebar'
import { useBranding } from '@/hooks/useBranding'
import { useSellerId } from '@/hooks/useSellerId'
import { useSellerFeatures } from '@/hooks/useSellerFeatures'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { GlassUIProvider } from '@/contexts/GlassUIContext'
import { useAffiliateTracking } from '@/hooks/useAffiliateTracking'
import { EditorUIProvider } from '@/contexts/EditorUIContext'
import { AccessGate } from '@/components/gates/AccessGate'
import { CompleteAccountAccessGate } from '@/components/gates/CompleteAccountAccessGate'
import PromanityShell from '@/components/promanity/layout/PromanityShell'
import { DemoProvider } from '@/components/demo/DemoProvider'
import { DemoEventBridge } from '@/components/demo/DemoEventBridge'
import CommunityDemoPlayer from '@/features/community/demo/CommunityDemoPlayer'
import CommunityDemoHighlight from '@/features/community/demo/CommunityDemoHighlight'
import AnnouncementLauncher from '@/components/announcements/AnnouncementLauncher'
import {
  FunnelBuilderModeProvider,
} from '@/contexts/FunnelBuilderModeContext'
import CookieConsentBanner from '@/components/legal/CookieConsentBanner'
import { FunnelGateProvider } from '@/contexts/FunnelGateContext'
import { PromanityExperienceProvider } from '@/contexts/promanity/PromanityExperienceProvider'
import { isPromanityDomain } from '@/lib/promanity-domains'
import {
  PromanityShellProvider,
} from '@/contexts/promanity/PromanityShellContext'
import {
  ThemeProvider,
  type Theme,
} from '@/contexts/ThemeContext'
import { InternalAppShell } from '@/components/layout/InternalAppShell'
import {
  InboxSocketProvider,
} from '@/contexts/InboxSocketContext'

type AccessContextType = {
  isAffiliateActive: boolean
  isOwner: boolean
  isAdmin: boolean
}

const AccessContext = createContext<AccessContextType>({
  isAffiliateActive: false,
  isOwner: false,
  isAdmin: false,
})
const ChannelProvider = dynamic(() => import('@/contexts/ChannelContext').then(m => m.ChannelProvider), { ssr: false })
const FunnelProvider = dynamic(() => import('@/contexts/FunnelContext').then(m => m.FunnelProvider), { ssr: false })
const FunnelStateProvider = dynamic(() => import('@/contexts/FunnelStateContext').then(m => m.FunnelStateProvider), { ssr: false })

export function Providers({
  children,
  modal,
  gate,
  isAffiliateActive,
}: {
  children:
    React.ReactNode

  modal?:
    React.ReactNode

  isAffiliateActive:
    boolean
  gate: {
    sellerId: number
    isOwner: boolean
    isAdmin: boolean
    accessStatus: string
    approvalRequired: boolean
    tag?: string
    access?: any
  }
}) {
  const [queryClient] = useState(() => new QueryClient())

  const { sellerId } = useSellerId()
  const { brand } = useBranding(sellerId)
  const { userId, user, loggedIn, theme } = useCurrentUser()
  const [resolvedTheme, setResolvedTheme] = useState<Theme>('light')
  const { features, loading } = useSellerFeatures(sellerId)

  useAffiliateTracking()

  const inviteUrl =
    loggedIn && user?.tracking_code
      ? `${window.location.origin}?invite=${user.tracking_code}`
      : null

  const pathname = usePathname()
  const cleanPath = pathname?.replace(/\/+$/, '') || '/'

  const brandDomain = String(brand?.customDomain || '')
    .replace(/^www\./, '')
    .toLowerCase()

  const [currentHostname, setCurrentHostname] = useState('')

  useEffect(() => {
    setCurrentHostname(window.location.hostname)
  }, [])

  const isPromanity = isPromanityDomain(currentHostname)

  //const isPromanity = false
  const isCRMRoute = isRoute(cleanPath, '/crm')
  const isDashboard = isRoute(cleanPath, '/dashboard')
  const isOnboardingRoute = isRoute(cleanPath, '/onboarding')
  const isPromanityDashboard = isDashboard && isPromanity

  const safeTheme = resolvedTheme === 'dark' ? 'dark' : 'light'

  const rawBackgroundImage =
    safeTheme === 'dark'
      ? brand?.background?.dark
      : brand?.background?.light

  const onboardingBackgroundImage =
    safeTheme === 'dark'
      ? brand?.onboardingBackground?.dark
      : brand?.onboardingBackground?.light

  const backgroundImage = isPromanityDashboard
    ? null
    : isOnboardingRoute
      ? onboardingBackgroundImage || rawBackgroundImage
      : rawBackgroundImage

  //const enableGlassUI = !!backgroundImage && !isPromanityDashboard

  const enableGlassUI = false

  function isRoute(pathname: string, route: string) {
    return pathname === route || pathname.startsWith(`${route}/`)
  }

  const isLegalPublicRoute =
    isRoute(cleanPath, '/terms') ||
    isRoute(cleanPath, '/privacy') ||
    isRoute(cleanPath, '/cookie-policy') ||
    isRoute(cleanPath, '/pricing-and-fees') ||
    isRoute(cleanPath, '/acceptable-use') ||
    isRoute(cleanPath, '/refund-policy') ||
    isRoute(cleanPath, '/compensation-plan') ||
    isRoute(cleanPath, '/income-disclaimer') ||
    isRoute(cleanPath, '/community-rules')

  const isPublicEventDetail =
    /^\/events\/[^/]+\/?$/.test(cleanPath)

  const isFullLayout =
    cleanPath === '' || cleanPath === '/' ||
    cleanPath.startsWith('/activate') ||
    cleanPath.startsWith('/aia') ||
    cleanPath.startsWith('/awaken') ||
    isRoute(cleanPath, '/f') ||
    isRoute(cleanPath, '/funnel-preview') ||
    isRoute(cleanPath, '/funnel-view') ||
    isRoute(cleanPath, '/funnel-preview') ||
    isRoute(cleanPath, '/product') ||
    isRoute(cleanPath, '/publication-share') ||
    isRoute(cleanPath, '/launch') ||
    /^\/(?:miniapp|resources|resource)\/[^/]+\/?$/.test(cleanPath) ||
    isRoute(cleanPath, '/setup') ||
    isRoute(cleanPath, '/start') ||
    isRoute(cleanPath, '/membership') ||
    isRoute(cleanPath, '/pricing') ||
    isRoute(cleanPath, '/login') ||
    isLegalPublicRoute ||
    (
      isPublicEventDetail &&
      !loggedIn
    )

  const shouldShowCookieBanner =
    isRoute(cleanPath, '/login') ||
    isRoute(cleanPath, '/membership') ||
    isRoute(cleanPath, '/pricing') ||
    isRoute(cleanPath, '/start') ||
    isRoute(cleanPath, '/setup') ||
    isLegalPublicRoute

  useEffect(() => {
    /*
    * Usuario loggeado:
    * su preferencia personal siempre gana.
    */
    if (
      loggedIn &&
      typeof theme === 'number'
    ) {
      setResolvedTheme(
        theme === 1
          ? 'dark'
          : 'light'
      )
      return
    }

    /*
    * Invitado:
    * usamos el default definido
    * por la comunidad.
    */
    if (!loggedIn) {
      const communityDefault =
        brand?.defaultThemeMode === 'dark'
          ? 'dark'
          : 'light'

      setResolvedTheme(
        communityDefault
      )
    }
  }, [
    loggedIn,
    theme,
    brand?.defaultThemeMode,
  ])

  useEffect(() => {
    document.documentElement.classList.toggle('dark', resolvedTheme === 'dark')
  }, [resolvedTheme])

  const toggleTheme =
    async () => {
    const nextTheme: Theme = resolvedTheme === 'dark' ? 'light' : 'dark'
    const nextNumeric = nextTheme === 'dark' ? 1 : 0

    setResolvedTheme(nextTheme)

      /*
      * Solo persistimos
      * preferencia personal
      * si existe un usuario.
      */
      if (loggedIn) {
        localStorage.setItem(
          'theme',
          nextTheme
        )

        await fetch(
          '/api/user/theme',
          {
            method:
              'POST',
            headers: {
              'Content-Type':
                'application/json',
            },
            body:
              JSON.stringify({
                theme:
                  nextNumeric,
              }),
          }
        )
      }
    }

  const shellProps = {
    children,
    gate,
    enableGlassUI,
    brand,
    backgroundImage,
    loggedIn,
    userId,
    features,
    isAffiliateActive,
    inviteUrl,
    isCRMRoute,
    isPromanityDashboard,
    sellerId,
  }

  if (isFullLayout) {
    return (
      <ThemeProvider
        theme={resolvedTheme}
        toggleTheme={toggleTheme}
      >
        <QueryClientProvider client={queryClient}>
          <GlassUIProvider enableGlassUI={enableGlassUI}>
            <TooltipProvider>
              <Sonner theme={resolvedTheme} />

              <FunnelProvider>
                <FunnelStateProvider>
                  {children}
                </FunnelStateProvider>
              </FunnelProvider>
              {shouldShowCookieBanner ? <CookieConsentBanner /> : null}
            </TooltipProvider>
          </GlassUIProvider>
        </QueryClientProvider>
      </ThemeProvider>
    )
  }

  return (    
    <AccessContext.Provider
      value={{
        isAffiliateActive,
        isOwner: gate.isOwner,
        isAdmin: gate.isAdmin,
      }}
    >
      <ThemeProvider
        theme={resolvedTheme}
        toggleTheme={toggleTheme}
      >
        <QueryClientProvider client={queryClient}>
          <GlassUIProvider enableGlassUI={enableGlassUI}>
            <TooltipProvider>
              <Sonner theme={resolvedTheme} />

              <AccessGate
                access={gate.access}
                isOwner={gate.isOwner}
                isAdmin={gate.isAdmin}
                sellerId={gate.sellerId}
                approvalRequired={gate.approvalRequired}
                tag={gate.tag || ''}
              />

              <CompleteAccountAccessGate
                allow={[
                  '/activate',
                  '/checkout',
                  '/thank-you',
                  '/order',
                  '/pricing',
                  '/membership',
                  '/product',
                ]}
              />

              <InboxSocketProvider
                userId={
                  loggedIn && userId
                    ? Number(userId)
                    : null
                }
              >

                <SidebarProvider defaultOpen>
                  <DemoProvider>
                  <DemoEventBridge />
                  <FunnelProvider>
                    <FunnelStateProvider>
                      <EditorUIProvider>
                        <ChannelProvider>
                          <AnnouncementLauncher />
                          <FunnelBuilderModeProvider>
                            <FunnelGateProvider
                              sellerId={Number(sellerId || gate.sellerId || 0)}
                              isOwner={gate.isOwner}
                            >
                            <>
                              {isPromanityDashboard ? (
                                <PromanityExperienceProvider
                                  isOwner={gate.isOwner}
                                  sellerId={Number(sellerId || gate.sellerId || 0)}
                                  userId={Number(userId || 0)}
                                >
                                  <PromanityShellProvider
                                    sellerId={Number(sellerId || gate.sellerId || 0)}
                                    loggedIn={loggedIn}
                                    userId={Number(userId || 0)}
                                    features={features}
                                    isAffiliateActive={isAffiliateActive}
                                    inviteUrl={inviteUrl}
                                  >
                                    <PromanityShell {...shellProps} />
                                  </PromanityShellProvider>
                                </PromanityExperienceProvider>
                              ) : (
                                <InternalAppShell
                                  children={children}
                                  gate={gate}
                                  enableGlassUI={enableGlassUI}
                                  brand={brand}
                                  backgroundImage={backgroundImage}
                                  loggedIn={loggedIn}
                                  userId={userId}
                                  features={features}
                                  isAffiliateActive={isAffiliateActive}
                                  inviteUrl={inviteUrl}
                                  isCRMRoute={isCRMRoute}
                                  isPromanityDashboard={false}
                                  sellerId={sellerId}
                                />
                              )}
                              {modal}
                            </>
                            </FunnelGateProvider>
                          </FunnelBuilderModeProvider>
                        </ChannelProvider>
                      </EditorUIProvider>
                    </FunnelStateProvider>
                  </FunnelProvider>
                  <CommunityDemoPlayer />
                  <CommunityDemoHighlight />
                  </DemoProvider>
                </SidebarProvider>
              </InboxSocketProvider>

              <CookieConsentBanner />
            </TooltipProvider>
          </GlassUIProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </AccessContext.Provider>
  )
}

export function useAccess() {
  return useContext(AccessContext)
}